import { Box, Chip, Stack, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { COLOR_FROM_CLASSIFICATION, ICON_FROM_CLASSIFICATION } from "library/SFG20/constants.mjs"
import { adjustLabels } from "routes/asset-register/plugins/regime-display/adjust-labels"
import { formatComparedDurations } from "lib/format-time"
import { TruncatedTypography } from "lib/@components/truncated-typography"

export function TailoredValue({ item: key, original, tailored, chipSx }) {
    let value = `${adjustLabels(key)}`
    let color = "info"

    switch (key) {
        case "suppressZeroU":
            value = (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Iconify icon="mdi:warning" />
                    <Typography variant="caption">Confirmed 0U</Typography>
                </Stack>
            )
            color = "error"
            break

        case "minutes": {
            const durations = formatComparedDurations(original.minutes, tailored.minutes)
            if (tailored.suppressZeroU) {
                value = null
            } else {
                if (!original.minutes) color = tailored.minutes ? "success" : "warning"
                value = (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption">Duration</Typography>
                        <strong data-cy="tailored-value-new-duration">{durations.to}</strong>
                        <Iconify icon="mdi:arrow-left" size={20} />
                        <Typography
                            variant="body2"
                            color="tailoredValueColors.text.lighter"
                            data-cy="tailored-value-original-duration"
                        >
                            {durations.from}
                        </Typography>
                    </Stack>
                )
            }
            break
        }
        case "frequency":
            if (tailored.suppressZeroU) {
                value = null
            } else {
                if (original.frequency.label === "0U") color = tailored.frequency.label === "0U" ? "warning" : "success"
                value = (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption">Frequency</Typography>
                        <strong>every {tailored.frequency.label}</strong> <Iconify icon="mdi:arrow-left" size={20} />
                        <Typography variant="body2" color="tailoredValueColors.text.lighter">
                            {original.frequency.label}
                        </Typography>
                    </Stack>
                )
            }
            break
        case "skill":
            value = (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption">Skill</Typography>
                    <TruncatedTypography
                        variant="body2"
                        color="tailoredValueColors.text.main"
                        sx={{ fontWeight: "bold" }}
                    >
                        {tailored.skill.Skilling ?? tailored.skill}
                    </TruncatedTypography>
                    <Iconify icon="mdi:arrow-left" size={20} />
                    <TruncatedTypography variant="body2" color="tailoredValueColors.text.lighter">
                        {original.skill.Skilling ?? original.skill}
                    </TruncatedTypography>
                </Stack>
            )
            break
        case "classification":
            value = (
                <Stack direction="row" spacing={1} alignItems="center" data-cy="tailored-value-criticality-chip">
                    <Typography variant="caption">Criticality</Typography>
                    <Iconify
                        sx={{ color: COLOR_FROM_CLASSIFICATION[tailored.classification] }}
                        icon={ICON_FROM_CLASSIFICATION[tailored.classification]}
                    />{" "}
                    <Typography variant="body2" color="tailoredValueColors.text.main" sx={{ fontWeight: "bold" }}>
                        {tailored.classification}
                    </Typography>{" "}
                    <Iconify icon="mdi:arrow-left" size={20} />
                    <Iconify
                        sx={{ color: COLOR_FROM_CLASSIFICATION[original.classification] }}
                        icon={ICON_FROM_CLASSIFICATION[original.classification]}
                    />{" "}
                    <Typography variant="body2" color="tailoredValueColors.text.lighter">
                        {original.classification}
                    </Typography>
                </Stack>
            )
            break
        case "included":
            value = (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Iconify icon="mdi:warning" />
                    <Typography variant="caption">Excluded</Typography>
                </Stack>
            )
            color = "error"
            break
        case "title":
            value = (
                <Stack direction="row" spacing={1} alignItems="center">
                    <TruncatedTypography
                        variant="body2"
                        color="tailoredValueColors.text.main"
                        sx={{ fontWeight: "bold" }}
                    >
                        {tailored.title}
                    </TruncatedTypography>
                    <Iconify icon="mdi:arrow-left" size={20} />
                    <TruncatedTypography variant="body2" color="tailoredValueColors.text.lighter">
                        {original.title}
                    </TruncatedTypography>
                </Stack>
            )
            break
        case "intro":
            value = (
                <Stack direction="row" spacing={1} alignItems="center">
                    <TruncatedTypography
                        variant="body2"
                        color="tailoredValueColors.text.main"
                        sx={{ fontWeight: "bold", wordWrap: "break-word" }}
                    >
                        Schedule Introduction/Notes Edited
                    </TruncatedTypography>
                </Stack>
            )
            break

        case "content":
            value = (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                        variant="body2"
                        color="tailoredValueColors.text.main"
                        sx={{ fontWeight: "bold", wordWrap: "break-word" }}
                    >
                        Task Content Edited
                    </Typography>
                </Stack>
            )
            break
        case "note":
            value = (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                        variant="body2"
                        color="tailoredValueColors.text.main"
                        sx={{ fontWeight: "bold", wordWrap: "break-word" }}
                    >
                        Task Notes Edited
                    </Typography>
                </Stack>
            )
            break
    }
    return !!value && <TailoredValueChip color={color} chipSx={chipSx} label={value} />
}

export function TailoredValueChip({ color, chipSx, label }) {
    return (
        <Box sx={{ py: 0.1 }}>
            <Chip
                size="small"
                sx={{
                    textTransform: "capitalize",
                    bgcolor: color ? `${color}.lighter` : "",
                    color: "tailoredValueColors.text.main",
                    ...chipSx,
                }}
                color={color}
                label={label}
                data-cy="tailored-value-chip"
            />
        </Box>
    )
}
